import React from 'react';

import type { OnUserFeedbackCallback } from '~/components/AiChat/components/Message/MessageTypes';
import { useChatMixpanel } from '~/components/AiChat/hooks/useChatMixpanel';
import CardDialog from '~/components/CardDialog';
import { FsButton, FsIconButton } from '~/components/core';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { Text } from '~/components/core/TextComponents';
import { ThumbDownIcon, ThumbUpIcon } from '~/components/icons';
import { getPopoverTriggerAria } from '~/components/ui/atoms/Popover/usePopoverState';
import { CLIVE_CHAT_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import cn from '~/Utils/cn';

import colors from '../../../../theme/tailwind/colors';

const FEEDBACK_TEXT = "Don't like this answer? Tell me why so I can improve";

const MessageFeedbackIconButton: React.FC<{
  tooltipText: string;
  onClick: OnUserFeedbackCallback;
  isSelectedValue: number;
  currentScore?: number;
  exchangeId: string;
  iconComponent: React.FC<{ iconColor?: string; size?: number }>;
  withText?: boolean;
  disabled?: boolean;
}> = ({
  tooltipText,
  onClick,
  isSelectedValue,
  currentScore,
  exchangeId,
  iconComponent,
  withText = false,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const popoverId = React.useId();
  const [feedbackText, setFeedbackText] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const { trackChatEvent } = useChatMixpanel();
  const isSelected = isSelectedValue === currentScore;
  const Icon = iconComponent;

  const showFeedbackDialog = (event: React.MouseEvent<HTMLElement>) => {
    if (!anchorEl) {
      trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_FEEDBACK_NEGATIVE_OPENED, { exchange_id: exchangeId });
    }
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const submitDialogHandler = () => {
    setIsSubmitting(true);
    setIsSubmitted(true);
    onClick(exchangeId, isSelectedValue, feedbackText);
    setIsSubmitting(false);
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isSelected || disabled) return;
    if (withText) {
      showFeedbackDialog(event);
      return;
    }

    // Submit feedback without text
    onClick(exchangeId, isSelectedValue, feedbackText);
  };

  return (
    <>
      <FsIconButton
        className={cn({
          ['cursor-default']: isSelected || isSubmitted || disabled,
          ['cursor-progress']: isSubmitting,
        })}
        tooltipText={tooltipText}
        onClick={handleClick}
        // Only a button that asks for written feedback opens a popover.
        {...(withText ? getPopoverTriggerAria(Boolean(anchorEl), popoverId) : {})}
      >
        <Icon size={20} iconColor={isSelected || isSubmitted ? colors.teal[700] : undefined} />
      </FsIconButton>

      <CardDialog
        anchorEl={anchorEl}
        isPopover
        open={!!anchorEl}
        id={popoverId}
        onClose={() => setAnchorEl(null)}
        closeOnBackdropClick
        title="I'd appreciate your feedback"
        // The popover's top center 40px below the button's top edge and 115px left of its left edge.
        side="bottom"
        sideOffset={({ anchor }) => 40 - anchor.height}
        alignOffset={({ anchor }) => -115 - anchor.width / 2}
      >
        <div className="__FEEDBACK_DIALOG_ROOT__ flex w-full flex-col gap-20 px-16">
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {FEEDBACK_TEXT}
          </Text>
          <TextField
            className="mb-16"
            variant="standard"
            multiline
            fullWidth
            placeholder="Add more details here (optional)"
            value={feedbackText}
            onChange={setFeedbackText}
            inputProps={{ maxLength: 10000 }}
          />
          <div className="flex w-full justify-end gap-12">
            <FsButton variant="text" color="primary" onClick={() => setAnchorEl(null)} disabled={isSubmitting}>
              Cancel
            </FsButton>
            <FsButton variant="contained" color="primary" onClick={submitDialogHandler} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </FsButton>
          </div>
        </div>
      </CardDialog>
    </>
  );
};

interface MessageFeedbackProps {
  onClick: OnUserFeedbackCallback;
  exchangeId: string;
  currentScore?: number;
  feedbackText?: string;
  disabled?: boolean;
}

export const PositiveFeedbackIconButton: React.FC<MessageFeedbackProps> = ({
  onClick,
  exchangeId,
  currentScore,
  disabled,
}) => (
  <MessageFeedbackIconButton
    tooltipText="This was a helpful response!"
    onClick={onClick}
    isSelectedValue={1}
    currentScore={currentScore}
    exchangeId={exchangeId}
    iconComponent={ThumbUpIcon}
    disabled={disabled}
  />
);

export const NegativeFeedbackIconButton: React.FC<MessageFeedbackProps> = ({
  onClick,
  exchangeId,
  currentScore,
  disabled,
}) => (
  <MessageFeedbackIconButton
    tooltipText="This is not helpful"
    onClick={onClick}
    isSelectedValue={-1}
    currentScore={currentScore}
    exchangeId={exchangeId}
    iconComponent={ThumbDownIcon}
    disabled={disabled}
    withText
  />
);
